/**
 * Локализация интерфейса (i18next + react-i18next).
 *
 * Устройство:
 *   - тексты лежат в `locales/<язык>/<пространство>.json`; пространство имён —
 *     по одному на страницу/компонент (`alerts`, `autoArbitrage`, …) плюс общее
 *     `common` для слов, которые встречаются везде («Сохранить», «Отмена»).
 *     Файлы подхватываются сборкой сами (import.meta.glob в lang-ru.ts /
 *     lang-uk.ts) — новый файл не надо нигде регистрировать;
 *   - язык по умолчанию — русский, он же запасной: ключ, которого нет в
 *     украинском словаре, показывается по-русски, а не кодом ключа;
 *   - выбор хранится в localStorage (`lang`) и уходит на сервер заголовком
 *     `X-Lang` (см. api/client.ts) — по нему сервер переводит свои тексты;
 *   - смена языка ПЕРЕЗАГРУЖАЕТ страницу (`setLanguage`). Это осознанно:
 *     часть подписей собирается на уровне модуля (списки вкладок, группы
 *     правил), и без перезагрузки они остались бы на прежнем языке.
 *
 * Загрузка словарей. Раньше оба языка (70 файлов × 2, ~230 КБ на язык) лежали
 * прямо в главном файле сборки: русский пользователь качал и разбирал
 * украинские тексты, и наоборот. Теперь каждый язык — свой чанк, и грузится
 * только нужный:
 *   - русский интерфейс — один запрос `lang-ru`;
 *   - украинский — `lang-uk` и ПАРАЛЛЕЛЬНО `lang-ru` (запасной). Ждём оба:
 *     подписи уровня модуля вычисляются один раз при импорте, и если бы
 *     запасной словарь приехал позже, пропущенный в украинском ключ так и
 *     остался бы кодом до перезагрузки. Запросы идут одновременно, так что
 *     ждём не дольше, чем самый медленный из двух.
 *
 * ВАЖНО: инициализация теперь АСИНХРОННАЯ. `initI18n()` обязана завершиться
 * ДО импорта приложения — иначе `i18n.t()` на уровне модуля вернёт код ключа.
 * Это делает main.tsx: сначала `await initI18n()`, потом `import('./boot')`.
 * Сам этот модуль ничего тяжёлого не тянет и импортируется где угодно.
 *
 * В компоненте:      const { t } = useTranslation('alerts');  t('notify.title')
 * Вне компонента:    import i18n from '../i18n';  i18n.t('alerts:notify.title')
 * Подстановки:       t('fits', { usd: 80 })          →  "влезает ${{usd}}"
 * Числа (1/2/5):     t('sessions', { count: n })     →  ключи sessions_one/_few/_many/_other
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

/** Словари одного языка: пространство имён → содержимое файла. */
type LangBundle = Record<string, Record<string, unknown>>;

/** `./locales/ru/alerts.json` → `{ alerts: {...} }`. */
function collect(files: Record<string, unknown>): LangBundle {
  const out: LangBundle = {};
  for (const [path, mod] of Object.entries(files)) {
    const m = path.match(/\/([^/]+)\.json$/);
    if (m) out[m[1]] = mod as Record<string, unknown>;
  }
  return out;
}

export const LANGS = ['ru', 'uk'] as const;
export type Lang = typeof LANGS[number];
/** Подпись языка в переключателе. */
export const LANG_LABEL: Record<Lang, string> = { ru: 'RU', uk: 'UA' };
export const LANG_NAME: Record<Lang, string> = { ru: 'Русский', uk: 'Українська' };

const STORAGE_KEY = 'lang';
/** Запасной язык: его ключами закрываются пропуски в остальных. */
const FALLBACK: Lang = 'ru';

function storedLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && (LANGS as readonly string[]).includes(v)) return v as Lang;
  } catch { /* приватный режим — живём без памяти */ }
  return 'ru';
}

/** Язык этой загрузки страницы. Не меняется до перезагрузки (см. setLanguage). */
const ACTIVE: Lang = storedLang();

// Каждый язык — отдельный чанк. Запись литералами, а не шаблонной строкой:
// так сборщик видит оба файла и даёт им предсказуемые имена (lang-ru-*.js),
// по которым их предзагружает index.html (см. vite.config.ts).
const LOADERS: Record<Lang, () => Promise<{ default: Record<string, unknown> }>> = {
  ru: () => import('./lang-ru'),
  uk: () => import('./lang-uk'),
};

async function loadLang(lang: Lang): Promise<LangBundle | null> {
  try {
    return collect((await LOADERS[lang]()).default);
  } catch (e) {
    // Чанк не доехал (обрыв сети, выкат посреди загрузки). Не роняем старт:
    // с одним словарём интерфейс читаем, без обоих — хотя бы работает.
    console.error(`i18n: словарь «${lang}» не загрузился`, e);
    return null;
  }
}

let ready: Promise<void> | null = null;

/** Загрузить словари активного языка (и запасного) и поднять i18next.
 *  Повторный вызов возвращает тот же промис. */
export function initI18n(): Promise<void> {
  if (ready) return ready;
  ready = (async () => {
    const langs: Lang[] = ACTIVE === FALLBACK ? [ACTIVE] : [ACTIVE, FALLBACK];
    const bundles = await Promise.all(langs.map(loadLang));
    const resources: Record<string, LangBundle> = {};
    langs.forEach((l, i) => { if (bundles[i]) resources[l] = bundles[i] as LangBundle; });
    const nsSource = resources[FALLBACK] || resources[ACTIVE] || { common: {} };
    await i18n.use(initReactI18next).init({
      resources: resources as any,
      lng: ACTIVE,
      fallbackLng: FALLBACK,
      defaultNS: 'common',
      fallbackNS: 'common',
      ns: Object.keys(nsSource),
      interpolation: { escapeValue: false },   // React сам экранирует
      returnNull: false,
      initImmediate: false,                    // словари уже загружены — без отложенного старта
      react: { useSuspense: false },
    });
    try { document.documentElement.lang = ACTIVE; } catch { /* нет документа */ }
  })();
  return ready;
}

/**
 * Словари одной страницы. В стартовый чанк языка они не входят (это 83 КБ из
 * ~290 КБ текстов — справка, ключи API, новая сессия, пресеты) и приезжают
 * перед кодом своей страницы: см. `withPageNs` и ленивые импорты в App.tsx.
 *
 * Сюда годится только пространство имён, которое читает ОДНА лениво
 * загружаемая страница. Если его начнёт читать что-то ещё (общий компонент,
 * шапка) — убрать из списка и из исключений в lang-ru.ts / lang-uk.ts, иначе
 * там вместо текста окажутся коды ключей.
 */
export const PAGE_NS = ['helpContent', 'apiKeys', 'autoSessionNew', 'venuePresets'] as const;
export type PageNs = typeof PAGE_NS[number];

const PAGE_FILES: Record<Lang, Record<string, () => Promise<unknown>>> = {
  ru: import.meta.glob([
    './locales/ru/helpContent.json', './locales/ru/apiKeys.json',
    './locales/ru/autoSessionNew.json', './locales/ru/venuePresets.json',
  ], { import: 'default' }),
  uk: import.meta.glob([
    './locales/uk/helpContent.json', './locales/uk/apiKeys.json',
    './locales/uk/autoSessionNew.json', './locales/uk/venuePresets.json',
  ], { import: 'default' }),
};

const pageNsLoads = new Map<PageNs, Promise<void>>();

/** Догрузить словарь страницы (активный язык и запасной — параллельно). */
export function loadPageNs(ns: PageNs): Promise<void> {
  let p = pageNsLoads.get(ns);
  if (!p) {
    const langs: Lang[] = ACTIVE === FALLBACK ? [ACTIVE] : [ACTIVE, FALLBACK];
    p = Promise.all(langs.map(async l => {
      const load = PAGE_FILES[l][`./locales/${l}/${ns}.json`];
      if (!load) return;   // в украинском файла может не быть — закроет запасной
      i18n.addResourceBundle(l, ns, await load(), true, true);
    })).then(() => undefined);
    // Неудачу не запоминаем: следующий заход на страницу попробует снова.
    p.catch(() => { pageNsLoads.delete(ns); });
    pageNsLoads.set(ns, p);
  }
  return p;
}

/**
 * Ленивый импорт страницы со своим словарём: сначала словарь, потом код.
 * Именно по очереди, не параллельно — у страниц есть подписи уровня модуля
 * (`i18n.t('apiKeys:…')` в константах), они вычисляются один раз при импорте
 * и без словаря остались бы кодами ключей. Словарь не доехал — ошибка уходит
 * в React.lazy так же, как если бы не доехал чанк самой страницы.
 */
export function withPageNs<T>(ns: PageNs, load: () => Promise<T>): () => Promise<T> {
  return async () => { await loadPageNs(ns); return load(); };
}

export function currentLang(): Lang {
  // До initI18n у i18next языка ещё нет — отвечаем сохранённым выбором, а не
  // «ru»: запросы к серверу (заголовок X-Lang) могут уйти раньше.
  const l = i18n.language || ACTIVE;
  return (LANGS as readonly string[]).includes(l) ? (l as Lang) : 'ru';
}

/** Сменить язык: запомнить и перезагрузить страницу (см. шапку файла). */
export function setLanguage(lang: Lang): void {
  if (lang === currentLang()) return;
  try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* не запомнится — и ладно */ }
  window.location.reload();
}

export default i18n;
